import React, { useRef, useEffect, useState, useCallback } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useReducedMotion,
} from "framer-motion";
import { experience } from "../portfolio";
import { blurIn, staggerContainer } from "../utils/motion";
import SpotlightCard from "./SpotlightCard";
import ScrambleText from "./ScrambleText";
import { playBeamSound } from "../utils/soundEngine";

function AnimatedMetric({ metric }) {
  const parsed = (() => {
    const match = metric.match(/^([^0-9]*)([0-9]+(?:\.[0-9]+)?)(.*)$/);
    if (!match) return { isNumber: false, text: metric };
    return {
      isNumber: true,
      prefix: match[1],
      target: parseFloat(match[2]),
      suffix: match[3],
      isFloat: match[2].includes("."),
    };
  })();

  const [val, setVal] = useState(0);
  const [hasTriggered, setHasTriggered] = useState(false);

  const handleInView = useCallback(() => {
    if (hasTriggered || !parsed.isNumber) return;
    setHasTriggered(true);

    let startTime;
    let frameId;
    const duration = 1200;

    const animate = (now) => {
      if (!startTime) startTime = now;
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      const current = parsed.target * eased;
      setVal(
        parsed.isFloat ? parseFloat(current.toFixed(1)) : Math.round(current)
      );
      if (progress < 1) frameId = requestAnimationFrame(animate);
    };

    frameId = requestAnimationFrame(animate);
    return () => {
      if (frameId) cancelAnimationFrame(frameId);
    };
  }, [hasTriggered, parsed]);

  return (
    <motion.span
      onViewportEnter={handleInView}
      viewport={{ once: true, amount: 0.2 }}
      className="inline-flex items-center px-2 py-0.5 rounded bg-surface/[0.04] border border-surface/[0.06] text-[10.5px] font-mono text-fg-muted tracking-tight hover:border-surface/[0.14] hover:text-fg transition-colors duration-200 tabular-nums"
    >
      {parsed.isNumber ? (
        <span>
          {parsed.prefix}
          {val}
          {parsed.suffix}
        </span>
      ) : (
        <span>{metric}</span>
      )}
    </motion.span>
  );
}

function TimelineNode({ isCurrent, logo, alt, color }) {
  const brandColor = color || "#009e60";

  return (
    <div className="relative flex flex-col items-center z-10">
      <motion.div
        whileHover={{ scale: 1.08 }}
        className={`w-12 h-12 rounded-full border-2 flex items-center justify-center overflow-hidden transition-all duration-500 ${
          isCurrent
            ? "bg-bg-elevated shadow-[0_0_18px_rgba(0,0,0,0.2)]"
            : "border-surface/[0.08] bg-bg-raised hover:border-surface/20"
        }`}
        style={{
          boxShadow: isCurrent ? `0 0 20px ${brandColor}40` : undefined,
          borderColor: isCurrent ? `${brandColor}80` : undefined,
        }}
      >
        {logo ? (
          <img src={logo} alt={alt} className="w-7 h-7 object-contain" />
        ) : (
          <div
            className={`w-3 h-3 rounded-full ${
              isCurrent ? "bg-fg" : "bg-fg-ghost"
            }`}
          />
        )}
      </motion.div>
      {isCurrent && (
        <div
          className="absolute inset-0 w-12 h-12 rounded-full border animate-ping opacity-30 pointer-events-none"
          style={{ borderColor: brandColor }}
        />
      )}
    </div>
  );
}

function ExperienceCard({ exp, index, isFirst }) {
  const isCurrent = exp.duration.includes("Present");

  const logoPath = (() => {
    try {
      return require(`../assests/images/${exp.logo_path}`);
    } catch {
      return null;
    }
  })();

  const brandColor = exp.color || "#009e60";

  return (
    <motion.div
      variants={blurIn(index % 2 === 0 ? "left" : "right", index * 0.08)}
      className="relative grid grid-cols-[48px_1fr] gap-6 group/exp"
    >
      {/* Node column — segments are invisible, just for spacing */}
      <div className="relative flex flex-col items-center">
        <div className={isFirst ? "h-6" : "h-6"} />
        <TimelineNode
          isCurrent={isCurrent}
          logo={logoPath}
          alt={exp.company}
          color={exp.color}
        />
        <div className="flex-1 min-h-[24px]" />
      </div>

      {/* Card */}
      <div className="pb-10 pt-5">
        <SpotlightCard
          enableHoverSound={false}
          className={`p-6 rounded-2xl border transition-all duration-400 ease-out-expo ${
            isCurrent
              ? "border-surface/[0.12] bg-surface/[0.035] shadow-[0_8px_32px_rgba(0,0,0,0.06)]"
              : "border-surface/[0.07] bg-surface/[0.015] hover:border-surface/[0.14] hover:bg-surface/[0.03]"
          }`}
          spotlightColor={`${brandColor}18`}
        >
          {/* Specular brand-tinted corner highlight */}
          <div
            className="absolute top-0 right-0 w-32 h-32 rounded-full blur-2xl pointer-events-none opacity-40 group-hover/exp:opacity-80 transition-opacity duration-500"
            style={{
              background: `radial-gradient(circle, ${brandColor}25, transparent 70%)`,
            }}
          />

          <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
            <div>
              <h3 className="font-display text-base sm:text-lg font-bold text-fg leading-snug">
                {exp.title}
              </h3>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
                <a
                  href={exp.company_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-fg-muted hover:text-fg transition-colors duration-300"
                >
                  {exp.company}
                </a>
                {exp.location && exp.location.trim() && (
                  <>
                    <span className="text-fg-ghost text-[10px]">&bull;</span>
                    <span className="text-xs text-fg-faint">
                      {exp.location}
                    </span>
                  </>
                )}
              </div>
            </div>

            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-surface/[0.04] border border-surface/[0.06]">
              {isCurrent && (
                <span
                  className="w-1.5 h-1.5 rounded-full animate-pulse"
                  style={{ backgroundColor: brandColor }}
                />
              )}
              <span className="text-[10.5px] font-mono text-fg-faint tracking-wide">
                {exp.duration}
              </span>
            </div>
          </div>

          {/* Metric Badges with animated counter roll-up */}
          {exp.metrics && exp.metrics.length > 0 && (
            <div className="flex flex-wrap gap-1.5 my-3.5">
              {exp.metrics.map((metric, mIdx) => (
                <AnimatedMetric key={mIdx} metric={metric} />
              ))}
            </div>
          )}

          <p className="text-xs sm:text-sm text-fg-muted leading-relaxed mt-2">
            {exp.description}
          </p>
        </SpotlightCard>
      </div>
    </motion.div>
  );
}

function SectionMarker({ title }) {
  return (
    <div className="relative grid grid-cols-[48px_1fr] gap-6 items-center">
      <div className="flex flex-col items-center">
        <div className="h-4" />
        <div className="w-2 h-2 rounded-sm bg-fg-ghost rotate-45 z-10" />
        <div className="h-4" />
      </div>
      <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-fg-faint py-2">
        {title}
      </span>
    </div>
  );
}

function ScrollBeam({ containerRef }) {
  const shouldReduce = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start 60%", "end 75%"],
  });

  const beamHeight = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  useEffect(() => {
    if (shouldReduce) return;
    let lastVal = 0;
    const threshold = 0.05;

    const handleChange = (latest) => {
      const delta = latest - lastVal;
      if (Math.abs(delta) >= threshold) {
        const direction = delta > 0 ? "down" : "up";
        playBeamSound(direction, latest);
        lastVal = latest;
      }
    };

    if (typeof scrollYProgress.on === "function") {
      return scrollYProgress.on("change", handleChange);
    } else if (typeof scrollYProgress.onChange === "function") {
      return scrollYProgress.onChange(handleChange);
    }
  }, [scrollYProgress, shouldReduce]);

  return (
    <>
      {/* Static background track */}
      <div
        className="absolute top-0 bottom-0 w-px bg-surface/[0.04] pointer-events-none"
        style={{ left: 24 }}
      />

      {/* Scroll-driven beam */}
      <div
        className="absolute top-0 bottom-0 w-px pointer-events-none z-[3] overflow-hidden"
        style={{ left: 24 }}
      >
        <motion.div
          style={{ height: beamHeight, willChange: "height" }}
          className="relative w-full"
        >
          <div className="absolute inset-0 bg-gradient-to-b from-surface/[0.06] via-surface/[0.1] to-transparent" />
          <div
            className="absolute bottom-0 left-0 right-0 bg-gradient-to-b from-transparent to-surface/30"
            style={{ height: 120 }}
          />
        </motion.div>
      </div>

      {/* Beam tip glow — desktop only (blur causes GPU trace on mobile) */}
      <motion.div
        style={{ top: beamHeight, left: 24, x: "-50%", y: "-50%" }}
        className="absolute z-[4] pointer-events-none hidden md:block"
      >
        <div className="w-5 h-5 rounded-full bg-surface/20 blur-[8px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[5px] h-[5px] rounded-full bg-surface/70" />
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 w-px h-8 bg-gradient-to-t from-surface/20 to-transparent" />
      </motion.div>
    </>
  );
}

function Experience() {
  const timelineRef = useRef(null);

  const allEntries = [];
  experience.sections.forEach((section, sIdx) => {
    allEntries.push({ type: "marker", title: section.title, key: `m-${sIdx}` });
    section.experiences.forEach((exp, eIdx) => {
      allEntries.push({
        type: "entry",
        exp,
        key: `e-${sIdx}-${eIdx}`,
        isFirst: sIdx === 0 && eIdx === 0,
      });
    });
  });

  return (
    <motion.div
      variants={staggerContainer()}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.05 }}
    >
      <motion.div variants={blurIn("up")} className="mb-14 text-center">
        <span className="section-label">Career</span>
        <h2 className="section-title">
          <ScrambleText text={experience.title} hover={true} />
        </h2>
        <p className="section-subtitle mx-auto">{experience.description}</p>
      </motion.div>

      <div ref={timelineRef} className="relative max-w-2xl mx-auto">
        <ScrollBeam containerRef={timelineRef} />

        {allEntries.map((item, i) =>
          item.type === "marker" ? (
            <SectionMarker key={item.key} title={item.title} />
          ) : (
            <ExperienceCard
              key={item.key}
              exp={item.exp}
              index={i}
              isFirst={item.isFirst}
            />
          )
        )}

        {/* Terminal node */}
        <div className="relative grid grid-cols-[48px_1fr] gap-6">
          <div className="flex flex-col items-center">
            <div className="h-4" />
            <div className="w-3 h-3 rounded-full border border-surface/[0.08] bg-bg z-10" />
          </div>
          <div />
        </div>
      </div>
    </motion.div>
  );
}

export default Experience;
